import React from "react";
import PropTypes from "prop-types";
import Geo from "@xo-union/sdk-geo";
const Context = /*#__PURE__*/React.createContext(null);

const CanSpamLoader = _ref => {
  let {
    geoClient,
    children
  } = _ref;
  const [state, setState] = React.useState({
    ready: false,
    requireExplicitOptin: false
  });
  const isMounted = React.useRef(false);
  React.useEffect(() => {
    isMounted.current = true;
    geoClient.geoByClientIP({
      country_only: true
    }).then(response => {
      if (!isMounted.current) {
        return;
      }

      const {
        countryCode
      } = response.body;
      setState({
        ready: true,
        requireExplicitOptin: countryCode !== 'US'
      });
    }).catch(() => {
      if (!isMounted.current) {
        return;
      }

      setState({
        ready: true,
        requireExplicitOptin: true
      });
    });
    return () => {
      isMounted.current = false;
    };
  }, [geoClient]);
  const finalChildren = typeof children === 'function' ? children(state) : children;
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: state
  }, finalChildren);
};

process.env.NODE_ENV !== "production" ? CanSpamLoader.propTypes = {
  geoClient: PropTypes.shape({
    geoByClientIP: PropTypes.func
  }).isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired
} : void 0;
/**
 *
 * This component handles country-specific spam laws by wrapping the logic to determine if the user should be automatically opted into marketing emails or if we require explicit optin.  The SignUp form depends on the data that's being provided by this component in order to conditionally display the email optin checkbox.
 * Ideally, you would mount this once at the root of the application (if you need to support sign up flow) as this makes a request to the Geo API to get the user's IP address.  Nested `CanSpam`s are allowed and will not result in duplicate requests.  Sibling `CanSpamProvider`s, however, will result in multiple requests to Geo API.
 *
 * #### Shared Context
 *
 * | Property              | Description                                    | Type  | Default |
 * |-----------------------------------------------------------------------------------------|
 * |`requireExplicitOptin`|Does user require explicit optin to TK emails?  | `bool`| `false`  |
 * |`ready`               |Has Geo API request completed? (success/failure)| `bool`| `false` |
 *
 */

const CanSpam = _ref2 => {
  let {
    children,
    geoClient
  } = _ref2;
  const canSpamState = React.useContext(Context);

  if (canSpamState) {
    return typeof children === 'function' ? children(canSpamState) : children;
  }

  return /*#__PURE__*/React.createElement(CanSpamLoader, {
    geoClient: geoClient
  }, children);
};

process.env.NODE_ENV !== "production" ? CanSpam.propTypes = {
  /**
   * Children or render function. The render function will receive the shared context as an argument
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,

  /**
   * Geo client from `@xo-union/sdk-geo`
   */
  geoClient: PropTypes.shape({
    geoByClientIP: PropTypes.func.isRequired
  })
} : void 0;
CanSpam.defaultProps = {
  geoClient: new Geo()
};
export default CanSpam;